import React, { useEffect, useState } from 'react';
import { api } from '../../utils/api';
import ModalOverlay from '../../components/ModalOverlay';
import './Admin.css';

const EMPTY = { no: '', name: '', contract_date: '', amount: '', org: '' };

export default function AdminPerformances() {
  const [perfs, setPerfs] = useState([]);
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadClients = () => api.getClients().then(setClients).catch(() => {});
  const load = () => api.getPerformances().then(setPerfs).catch(() => {});
  useEffect(() => {
    load();
    loadClients();
  }, []);

  const openCreate = () => { setForm(EMPTY); setError(''); setModal('create'); };
  const openEdit = (p) => {
    setForm({ no: p.no, name: p.name, contract_date: p.contract_date, amount: p.amount, org: p.org });
    setError('');
    setModal(p.id);
  };

  const save = async (e) => {
    e.preventDefault();
    setError('');

    const noNum = Number(form.no);
    const noDuplicate = perfs.some((p) => p.no === noNum && p.id !== modal);
    if (noDuplicate) {
      setError(`번호 ${noNum}은(는) 이미 사용 중입니다.`);
      return;
    }

    const orgName = form.org.trim();
    const isKnownClient = clients.some((c) => c.name === orgName);
    if (!isKnownClient) {
      setError('고객사 관리에 등록된 기관만 선택할 수 있습니다. 목록에 없다면 먼저 고객사 관리에서 등록해주세요.');
      return;
    }

    setSaving(true);
    try {
      const data = { ...form, no: noNum, amount: Number(form.amount) };
      if (modal === 'create') await api.createPerformance(data);
      else await api.updatePerformance(modal, data);
      setModal(null);
      load();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await api.deletePerformance(id);
    load();
  };

  const fmtYear = (d) => (d === '진행중' ? d : d.substring(0, 4));

  return (
    <div>
      <div className="admin-page-header">
        <div className="admin-page-title">감리 실적</div>
        <div className="admin-page-sub">수행 실적을 추가·수정·삭제합니다.</div>
      </div>

      <div className="admin-card">
        <div className="admin-toolbar">
          <span style={{ color: '#64748b', fontSize: '0.875rem' }}>총 {perfs.length}건</span>
          <button className="btn btn-primary" onClick={openCreate}>+ 실적 추가</button>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>번호</th>
                <th>사업명</th>
                <th>계약연도</th>
                <th>금액(원)</th>
                <th>주관기관</th>
                <th>작업</th>
              </tr>
            </thead>
            <tbody>
              {perfs.map((p) => (
                <tr key={p.id}>
                  <td>{p.no}</td>
                  <td style={{ maxWidth: 360 }}>{p.name}</td>
                  <td>
                    {p.contract_date === '진행중'
                      ? <span className="badge badge-active">진행중</span>
                      : fmtYear(p.contract_date)}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>{p.amount.toLocaleString()}</td>
                  <td>{p.org}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(p)}>수정</button>
                    {' '}
                    <button className="btn btn-danger btn-sm" onClick={() => remove(p.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modal !== null && (
        <ModalOverlay onClose={() => setModal(null)}>
          <div className="modal-title">{modal === 'create' ? '실적 추가' : '실적 수정'}</div>
            <form onSubmit={save}>
              <div className="form-group">
                <label className="form-label">번호</label>
                <input className="form-input" type="number" value={form.no}
                  onChange={(e) => setForm((f) => ({ ...f, no: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">사업명</label>
                <input className="form-input" type="text" value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">계약일자 (예: 2025.12.31 또는 진행중)</label>
                <input className="form-input" type="text" value={form.contract_date}
                  onChange={(e) => setForm((f) => ({ ...f, contract_date: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">금액 (원)</label>
                <input className="form-input" type="number" value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">주관기관</label>
                <input className="form-input" type="text" list="org-options" value={form.org}
                  onChange={(e) => setForm((f) => ({ ...f, org: e.target.value }))}
                  placeholder="고객사 검색" required />
                <datalist id="org-options">
                  {clients.map((c) => <option key={c.id} value={c.name} />)}
                </datalist>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 4 }}>
                  목록에 없는 고객사는 고객사 관리에서 먼저 등록해주세요.
                </div>
              </div>
              {error && <div className="form-error">{error}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>취소</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? '저장 중...' : '저장'}
                </button>
              </div>
            </form>
        </ModalOverlay>
      )}
    </div>
  );
}
